/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jan 02 2024
 **/

import { useTheme } from "@emotion/react";

import { Avatar, Row, Typography, TypographyTooltip } from "@/frameworks/designStuff/components";
import { AvatarGroup } from "@/frameworks/designStuff/compounds";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { useProperties } from "@/frameworks/libraries/properties";
import { useFileManager } from "@/frameworks/core/hooks";
import { Avatar as CoreAvatar } from "@/frameworks/core/components";
import { SwitchCase } from "@/frameworks/libraries/components";

import * as S from "./emotion";
import { usePersistCurrentSpace } from "@/frameworks/core/persistence";

function All({ fileBoxType }: { fileBoxType: "All" | "Space" | "DM" }) {
	const theme = useTheme();
	const { setCurrentFolder, currentFolder } = useFileManager();

	const handleClick = () => {
		switch (fileBoxType) {
			case "All": {
				setCurrentFolder({
					id: fileBoxType,
					name: _T("전체"),
					isMyFile: false,
					referenceType: undefined,
					referenceID: undefined,
					all: true,
					folderType: "ALL"
				});
				break;
			}
			case "DM": {
				setCurrentFolder({
					id: fileBoxType,
					name: _T("전체 다이렉트 메시지"),
					isMyFile: false,
					referenceType: undefined,
					referenceID: undefined,
					all: true,
					folderType: "CHAT"
				});
				break;
			}
			case "Space": {
				setCurrentFolder({
					id: fileBoxType,
					name: _T("전체 스페이스"),
					isMyFile: false,
					referenceType: undefined,
					referenceID: undefined,
					all: true,
					folderType: "SPACE"
				});
				break;
			}
		}
	};

	return (
		<S.FileBoxWrapper
			focus={currentFolder.id === fileBoxType}
			verticalAlign="center"
			horizonAlign="distribute"
			onClick={handleClick}
		>
			<Row gap={12}>
				<Row
					verticalAlign="center"
					horizonAlign="center"
					css={{
						width: "24px",
						height: "24px",
						background: theme.colors.gradient_40_100_w50,
						borderRadius: "8px"
					}}
				>
					<Typography typoColor="accent_60_100" typoSize="Body3_MD">
						All
					</Typography>
				</Row>
				<Typography typoColor="neutral_80_100" typoSize="Body3_MD">
					<SwitchCase
						condition={fileBoxType}
						cases={{
							All: <>{_T("전체")}</>,
							DM: <>{_T("전체 다이렉트 메시지")}</>,
							Space: <>{_T("전체 스페이스")}</>
						}}
					/>
				</Typography>
			</Row>
		</S.FileBoxWrapper>
	);
}

function Space({ space }: { space: Required<FileBox.Folders.GetFoldersResponse>["spaces"][number] }) {
	const { setCurrentFolder, currentFolder } = useFileManager();
	const properties = useProperties();
	const currentSpace = usePersistCurrentSpace();
	const isEntered = space.spaceId === currentSpace?.spaceId;

	const focus = () => {
		if (currentFolder.id === space.spaceId) {
			if (!currentFolder.isMyFile) {
				return true;
			}
			return false;
		}
		return false;
	};

	return (
		<S.FileBoxWrapper
			focus={focus()}
			verticalAlign="center"
			horizonAlign="distribute"
			hoverBackground="accent_10_100"
			onClick={() => {
				setCurrentFolder({
					id: space.spaceId,
					name: `${space.spaceType === "DEFAULT" ? space.groupName : ""} ${space.myRoom ? _T("마이룸") : space.spaceName}`,
					isMyFile: false,
					referenceType: space.myRoom ? "INDIVIDUAL" : "SPACE",
					referenceID: space.spaceId,
					all: false,
					folderType: "SPACE",
					myRoomOnly: space.myRoom
				});
			}}
		>
			<Row gap={12} css={{ width: "100%" }}>
				<Avatar
					type="image"
					kind="people_24"
					text={space.spaceName || ""}
					src={`${properties.default.webDomain}${space.thumbnailPath}`}
				/>
				<S.SpaceName
					deleted={space.deleted}
					isEntered={isEntered}
					maxLine={1}
					align="left"
					ellipsis
					typoColor="neutral_80_100"
					typoSize="Body3_MD"
				>
					{space.spaceType === "DEFAULT" && space.groupName} {space.myRoom ? _T("마이룸") : space.spaceName}
				</S.SpaceName>
				{space.deleted && (
					<S.SpaceStatusChip status="deleted" typoSize="Body7" typoColor="neutral_00_100">
						{_T("삭제됨")}
					</S.SpaceStatusChip>
				)}
				{isEntered && (
					<S.SpaceStatusChip status="participated" typoSize="Body7" typoColor="neutral_00_100">
						{_T("접속중")}
					</S.SpaceStatusChip>
				)}
			</Row>
		</S.FileBoxWrapper>
	);
}

function DM({ dm }: { dm: Required<FileBox.Folders.GetFoldersResponse>["chatRooms"][number]["dm"] }) {
	const { setCurrentFolder, currentFolder } = useFileManager();

	if (!dm) return;

	const deletedCount = dm.deletedUserCount || 0;
	const isToSelf = dm.users.length === 1 && deletedCount === 0;

	const userList = isToSelf ? dm.users : dm.users.filter((user) => user.id !== Configure.userID);
	const deletedUserList = Array.from({ length: deletedCount }, () => ({ id: "", name: _T("삭제된 사용자") }));
	const users = [...userList, ...deletedUserList];
	const userNames = users.map(({ name }) => name).join(", ");

	return (
		<S.FileBoxWrapper
			focus={currentFolder.id === dm.roomId}
			verticalAlign="center"
			horizonAlign="distribute"
			onClick={() => {
				setCurrentFolder({
					id: dm.roomId,
					name: userNames,
					isMyFile: false,
					referenceType: "CHAT",
					referenceID: dm.roomId,
					all: false,
					folderType: "CHAT"
				});
			}}
		>
			<Row gap={12} css={{ width: "100%" }}>
				<AvatarGroup
					size="24px"
					avatarList={users.map((avatar, index) => {
						return (
							<CoreAvatar
								key={index}
								kind="people_24"
								{...avatar}
								showMiniProfile={false}
								useStatus={users.length === 1}
							/>
						);
					})}
				/>
				<TypographyTooltip.p
					tooltipProps={{
						hasIcon: true
					}}
					componentProps={{
						typoSize: "Body3_MD",
						typoColor: "neutral_80_100",
						ellipsis: true,
						maxLine: 1,
						stretch: true,
						align: "left"
					}}
				>
					{userNames}
				</TypographyTooltip.p>
			</Row>
		</S.FileBoxWrapper>
	);
}

type SpaceFileBox = {
	type: "Space";
} & Required<FileBox.Folders.GetFoldersResponse>["spaces"][number];

type DMFileBox = {
	type: "DM";
} & Required<FileBox.Folders.GetFoldersResponse>["chatRooms"][number]["dm"];

type AllFileBox = {
	type: "All";
	fileBoxType: "All" | "Space" | "DM";
};

type FileBoxProps = SpaceFileBox | DMFileBox | AllFileBox;

export default function Folder({ type, ...rest }: FileBoxProps) {
	if (type === "DM") {
		return <DM dm={rest as Omit<DMFileBox, "type">} />;
	}
	if (type === "Space") {
		return <Space space={rest as Omit<SpaceFileBox, "type">} />;
	}
	if (type === "All") {
		return <All fileBoxType={(rest as Omit<AllFileBox, "type">).fileBoxType} />;
	}
}
